import { useEffect, useState } from "react";
import { Star, Eye, EyeOff, Trash2, MessageCircle, Loader2, Reply } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { CORRIDORS, formatDate } from "@/lib/constants";

export default function AdminReviews() {
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const status = filter !== "all" ? `?status=${filter}` : "";
      const { data } = await api.get(`/admin/reviews${status}`);
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, [filter]);

  const visibleCount = items.filter((r) => r.status === "visible").length;
  const hiddenCount = items.filter((r) => r.status === "hidden").length;
  const avg = items.length ? (items.filter((r) => r.status === "visible").reduce((s, r) => s + r.rating, 0) / Math.max(1, visibleCount)) : 0;

  return (
    <div data-testid="admin-reviews-page">
      <div className="flex items-center gap-3 mb-6">
        <span className="w-10 h-10 rounded-xl bg-[var(--sl-primary)]/10 grid place-items-center text-[var(--sl-primary)]"><Star size={18} /></span>
        <div>
          <h1 className="font-heading text-2xl font-bold">Avis clients</h1>
          <p className="text-sm text-gray-500">Modération des avis publiés sur le site.</p>
        </div>
      </div>

      {/* Aggregate stats */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <StatTile label="Avis visibles" value={String(visibleCount)} />
        <StatTile label="Avis masqués" value={String(hiddenCount)} />
        <StatTile label="Moyenne (visibles)" value={visibleCount ? `${avg.toFixed(2)}/5` : "—"} highlight />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {[
          ["all", "Tous"],
          ["visible", "Visibles"],
          ["hidden", "Masqués"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={`inline-flex items-center gap-1 rounded-full text-sm font-medium px-4 py-2 transition ${
              filter === key
                ? "bg-[var(--sl-primary)] text-white shadow"
                : "bg-white border border-gray-200 text-gray-700 hover:border-[var(--sl-primary)]/40"
            }`}
            data-testid={`reviews-filter-${key}`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="sl-card text-center text-gray-500">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="sl-card text-center text-sm text-gray-500" data-testid="reviews-empty">Aucun avis dans cette catégorie.</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {items.map((r) => <AdminReviewCard key={r.id} r={r} onChange={refresh} />)}
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, highlight }) {
  return (
    <div className="sl-card !p-3 sm:!p-4">
      <div className="text-[10px] sm:text-xs uppercase tracking-widest text-gray-500 font-semibold">{label}</div>
      <div className={`font-heading text-lg sm:text-2xl font-bold mt-1 ${highlight ? "text-[var(--sl-primary)]" : "text-gray-900"}`}>{value}</div>
    </div>
  );
}

function AdminReviewCard({ r, onChange }) {
  const [acting, setActing] = useState(null);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState(r.reply || "");
  const [savingReply, setSavingReply] = useState(false);
  const corridor = CORRIDORS[r.corridor];

  const setVisibility = async (status) => {
    setActing(status);
    try {
      await api.patch(`/admin/reviews/${r.id}`, { status });
      toast.success(status === "visible" ? "Avis rendu visible" : "Avis masqué");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setActing(null); }
  };

  const saveReply = async () => {
    setSavingReply(true);
    try {
      await api.patch(`/admin/reviews/${r.id}/reply`, { reply: replyText.trim() });
      toast.success(replyText.trim() ? "Réponse publiée" : "Réponse supprimée");
      setReplyOpen(false);
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSavingReply(false); }
  };

  const del = async () => {
    if (!window.confirm("Supprimer définitivement cet avis ?")) return;
    setActing("delete");
    try {
      await api.delete(`/admin/reviews/${r.id}`);
      toast.success("Avis supprimé");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setActing(null); }
  };

  return (
    <article className={`sl-card ${r.status === "hidden" ? "opacity-60" : ""}`} data-testid={`admin-review-${r.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="font-semibold text-sm">{r.name}</div>
          <div className="text-[10px] text-gray-400 uppercase tracking-widest">
            {corridor?.shortLabel || corridor?.label || r.corridor} · {formatDate(r.created_at)}
          </div>
        </div>
        <Stars value={r.rating} />
      </div>

      <p className="text-sm text-gray-700 leading-relaxed mt-2 line-clamp-6 whitespace-pre-line">{r.comment}</p>

      <div className="text-[10px] text-gray-400 mt-3 font-mono flex items-center gap-1">
        <MessageCircle size={10} /> Tx réf. {r.transaction_code} · {r.user_email || "invité"}
      </div>

      {/* Team reply (existing) */}
      {r.reply && !replyOpen && (
        <div className="mt-3 rounded-xl bg-[var(--sl-primary)]/5 border border-[var(--sl-primary)]/15 p-3" data-testid={`admin-review-reply-${r.id}`}>
          <div className="text-[10px] uppercase tracking-widest text-[var(--sl-primary)] font-semibold flex items-center gap-1 mb-1">
            <Reply size={11} /> Réponse de l&apos;équipe
          </div>
          <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-line">{r.reply}</p>
        </div>
      )}

      {/* Reply editor */}
      {replyOpen && (
        <div className="mt-3" data-testid={`admin-review-reply-editor-${r.id}`}>
          <textarea
            rows={3}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            maxLength={1000}
            placeholder="Répondez publiquement à ce client…"
            className="sl-input text-sm"
            data-testid={`review-reply-input-${r.id}`}
          />
          <div className="flex items-center justify-end gap-2 mt-2">
            <button onClick={() => { setReplyOpen(false); setReplyText(r.reply || ""); }} className="text-xs sl-btn-outline" data-testid={`review-reply-cancel-${r.id}`}>Annuler</button>
            <button onClick={saveReply} disabled={savingReply} className="text-xs sl-btn-primary disabled:opacity-60" data-testid={`review-reply-save-${r.id}`}>
              {savingReply ? <Loader2 size={11} className="animate-spin" /> : <Reply size={11} />} {r.reply ? "Mettre à jour" : "Publier la réponse"}
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-1.5">
        {r.status === "visible" ? (
          <button onClick={() => setVisibility("hidden")} disabled={!!acting} className="text-xs inline-flex items-center gap-1 rounded-full border border-amber-200 text-amber-700 px-2.5 py-1 hover:bg-amber-50 disabled:opacity-60" data-testid={`review-hide-${r.id}`}>
            {acting === "hidden" ? <Loader2 size={10} className="animate-spin" /> : <EyeOff size={10} />} Masquer
          </button>
        ) : (
          <button onClick={() => setVisibility("visible")} disabled={!!acting} className="text-xs inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-2.5 py-1 hover:bg-emerald-700 disabled:opacity-60" data-testid={`review-show-${r.id}`}>
            {acting === "visible" ? <Loader2 size={10} className="animate-spin" /> : <Eye size={10} />} Publier
          </button>
        )}
        {!replyOpen && (
          <button onClick={() => setReplyOpen(true)} disabled={!!acting} className="text-xs inline-flex items-center gap-1 rounded-full border border-[var(--sl-primary)]/30 text-[var(--sl-primary)] px-2.5 py-1 hover:bg-[var(--sl-primary)]/10 disabled:opacity-60" data-testid={`review-reply-${r.id}`}>
            <Reply size={10} /> {r.reply ? "Modifier la réponse" : "Répondre"}
          </button>
        )}
        <button onClick={del} disabled={!!acting} className="text-xs inline-flex items-center gap-1 rounded-full border border-red-200 text-red-700 px-2.5 py-1 hover:bg-red-50 disabled:opacity-60" data-testid={`review-delete-${r.id}`}>
          {acting === "delete" ? <Loader2 size={10} className="animate-spin" /> : <Trash2 size={10} />} Supprimer
        </button>
      </div>
    </article>
  );
}

function Stars({ value }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={13}
          className={i <= Math.round(value) ? "text-amber-500" : "text-gray-300"}
          fill={i <= Math.round(value) ? "currentColor" : "none"}
        />
      ))}
    </div>
  );
}
